"use client";

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { handleMultiSelect } from "./utils";
import type { ClauseFilters } from "./types";

const INSTRUMENT_TYPE_OPTIONS = ["Bond", "Loan"];

const hideMissingOptions = (options: string[]) =>
  options.filter((option) => !option.includes("Missing"));

const HIDDEN_CREDITOR_TYPE_OPTIONS = new Set([
  "Banks, Bilateral Official, Multilateral Official",
  "Banks, Multilateral Official",
  "Capital markets, Private Bond Holder",
]);

const hideExcludedCreditorTypeOptions = (options: string[]) =>
  options.filter((option) => !HIDDEN_CREDITOR_TYPE_OPTIONS.has(option));

const CREDITOR_TYPE_LABEL_MAP: Record<string, string> = {
  "Banks, Bilateral Official": "Multiple Creditor Types",
  "Capital markets": "Capital Markets",
};

const getCreditorTypeLabel = (value: string): string =>
  CREDITOR_TYPE_LABEL_MAP[value] ?? value;

type FiltersPanelClauseProps = {
  filters: ClauseFilters;
  setFilters: (next: ClauseFilters) => void;
  creditorRegionOptions: string[];
  borrowerRegionOptions: string[];
  creditorTypeColumns: string[];
  denominationColumns: string[];
  minYear: number;
  maxYear: number;
  sticky?: boolean;
  horizontal?: boolean;
  noCard?: boolean;
};

export default function FiltersPanelClause({
  filters,
  setFilters,
  creditorRegionOptions,
  borrowerRegionOptions,
  creditorTypeColumns,
  denominationColumns,
  minYear,
  maxYear,
  sticky = true,
  horizontal = false,
  noCard = false,
}: FiltersPanelClauseProps) {
  const visibleCreditorRegionOptions = hideMissingOptions(
    creditorRegionOptions,
  );
  const visibleBorrowerRegionOptions = hideMissingOptions(
    borrowerRegionOptions,
  );
  const visibleCreditorTypeColumns = hideExcludedCreditorTypeOptions(
    hideMissingOptions(creditorTypeColumns),
  );
  const visibleDenominationColumns = hideMissingOptions(denominationColumns);

  const instrumentTypeSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Instrument Type</InputLabel>
      <Select
        multiple
        value={filters.selectedInstrumentTypes}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedInstrumentTypes: next }),
          )
        }
        input={<OutlinedInput label="Instrument Type" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {INSTRUMENT_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedInstrumentTypes.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const creditorRegionSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Creditor Region</InputLabel>
      <Select
        multiple
        value={filters.selectedCreditorRegions}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedCreditorRegions: next }),
          )
        }
        input={<OutlinedInput label="Creditor Region" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {visibleCreditorRegionOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedCreditorRegions.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const borrowerRegionSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Borrower Region</InputLabel>
      <Select
        multiple
        value={filters.selectedBorrowerRegions}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedBorrowerRegions: next }),
          )
        }
        input={<OutlinedInput label="Borrower Region" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {visibleBorrowerRegionOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedBorrowerRegions.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const creditorTypeSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Creditor Type</InputLabel>
      <Select
        multiple
        value={filters.selectedCreditorTypes}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedCreditorTypes: next }),
          )
        }
        input={<OutlinedInput label="Creditor Type" />}
        renderValue={(selected) =>
          selected.map((value) => getCreditorTypeLabel(value)).join(", ")
        }
      >
        {visibleCreditorTypeColumns.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedCreditorTypes.includes(option)}
            />
            <ListItemText primary={getCreditorTypeLabel(option)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const denominationSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Denomination</InputLabel>
      <Select
        multiple
        value={filters.selectedDenominations}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedDenominations: next }),
          )
        }
        input={<OutlinedInput label="Denomination" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {visibleDenominationColumns.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedDenominations.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const dateRangeSlider = (
    <Box sx={horizontal ? { width: "300px" } : {}}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0 }}>
        Date Range: {filters.yearRange[0]} - {filters.yearRange[1]}
      </Typography>
      <Slider
        value={filters.yearRange}
        min={minYear}
        max={maxYear}
        onChange={(_, value) =>
          setFilters({ ...filters, yearRange: value as number[] })
        }
        valueLabelDisplay="auto"
      />
    </Box>
  );

  const completeDataToggle = (
    <Box
      sx={
        horizontal
          ? {
              display: "flex",
              alignItems: "center",
              gap: 1,
              minHeight: 40,
              marginLeft: "2rem",
            }
          : {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }
      }
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Complete Metadata Only
      </Typography>
      <Switch
        checked={filters.completeDataOnly}
        onChange={(event) =>
          setFilters({ ...filters, completeDataOnly: event.target.checked })
        }
      />
    </Box>
  );

  const handleReset = () => {
    setFilters({
      selectedInstrumentTypes: [],
      selectedCreditorRegions: [],
      selectedBorrowerRegions: [],
      selectedCreditorTypes: [],
      selectedDenominations: [],
      yearRange: [minYear, maxYear],
      completeDataOnly: false,
    });
  };

  const resetButton = (
    <Button
      variant="outlined"
      size="small"
      onClick={handleReset}
      sx={{ minWidth: 92 }}
    >
      Reset
    </Button>
  );

  const inner = horizontal ? (
    <Stack spacing={2.2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "repeat(5, 1fr)",
          },
          gap: 2,
        }}
      >
        {instrumentTypeSelect}
        {creditorRegionSelect}
        {borrowerRegionSelect}
        {creditorTypeSelect}
        {denominationSelect}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        {dateRangeSlider}
        {completeDataToggle}
        {resetButton}
      </Box>
    </Stack>
  ) : (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Filters
      </Typography>
      <Stack spacing={2.2}>
        {instrumentTypeSelect}
        {creditorRegionSelect}
        {borrowerRegionSelect}
        {creditorTypeSelect}
        {denominationSelect}
        {dateRangeSlider}
        {completeDataToggle}
        {resetButton}
      </Stack>
    </>
  );

  if (noCard) {
    return inner;
  }

  return (
    <Card
      sx={{
        height: "fit-content",
        position: sticky ? { lg: "sticky" } : "static",
        top: sticky ? { lg: 16 } : "auto",
        boxShadow: "none",
        marginTop: "2rem",
      }}
    >
      <CardContent>{inner}</CardContent>
    </Card>
  );
}
